import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { useTranslation } from "react-i18next";
import { get } from "../http/api";

const REMINDER_KEY = "carrerup_premium_reminder";
const REMINDER_DELAY = 4 * 24 * 60 * 60 * 1000; // 7 jours

const translations = {
  fr: {
    badge: "CarrerUp Premium",
    title: "Passez au niveau supérieur",
    subtitle:
      "Développez votre réseau et profitez pleinement de CarrerUp.",
    visitors: "Découvrez qui consulte votre profil",
    search: "Accédez à la recherche avancée",
    matching: "Matchez et débloquez la messagerie",
    discover: "Découvrir Premium",
    later: "Plus tard"
  },

  en: {
    badge: "CarrerUp Premium",
    title: "Take your profile further",
    subtitle:
      "Grow your network and unlock the full CarrerUp experience.",
    visitors: "See who's viewing your profile",
    search: "Access advanced search",
    matching: "Match and unlock messaging",
    discover: "Discover Premium",
    later: "Maybe later"
  },

  es: {
    badge: "CarrerUp Premium",
    title: "Lleva tu perfil más lejos",
    subtitle:
      "Amplía tu red y disfruta de todo el potencial de CarrerUp.",
    visitors: "Descubre quién visita tu perfil",
    search: "Accede a la búsqueda avanzada",
    matching: "Haz match y desbloquea la mensajería",
    discover: "Descubrir Premium",
    later: "Más tarde"
  },

  ar: {
    badge: "CarrerUp Premium",
    title: "ارتقِ بحسابك إلى المستوى التالي",
    subtitle:
      "وسّع شبكة علاقاتك واستفد من جميع مزايا CarrerUp.",
    visitors: "اعرف من شاهد ملفك الشخصي",
    search: "استخدم البحث المتقدم",
    matching: "طابق مع الآخرين وافتح المراسلة",
    discover: "اكتشف Premium",
    later: "لاحقاً"
  }
};

export default function PremiumReminderModal() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "fr"
  ).split("-")[0];

  const t = translations[language] || translations.fr;

  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer;

    async function checkPremium() {
      try {
        // Ne pas afficher si l'utilisateur a choisi "Plus tard"
        // au cours des 7 derniers jours.
        const lastReminder = Number(
          localStorage.getItem(REMINDER_KEY) || 0
        );

        if (
          lastReminder &&
          Date.now() - lastReminder < REMINDER_DELAY
        ) {
          return;
        }

        // Source de vérité : le backend
        const response = await get("billing/status");

        if (cancelled) return;

        if (response.data?.premium === true) {
          return;
        }

        // On laisse d'abord le Feed apparaître.
        timer = setTimeout(() => {
          if (!cancelled) {
            setOpen(true);
          }
        }, 1800);
      } catch (error) {
        // En cas d'erreur réseau/billing :
        // ne jamais bloquer l'utilisateur avec le modal.
        console.warn(
          "[PremiumReminder] Unable to check Premium status"
        );
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    }

    checkPremium();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const remindLater = () => {
    localStorage.setItem(
      REMINDER_KEY,
      String(Date.now())
    );

    setOpen(false);
  };

  const discoverPremium = () => {
    setOpen(false);
    navigate("/billing");
  };

  if (checking) return null;

  return (
    <Dialog
      open={open}
      onClose={remindLater}
      maxWidth="xs"
      fullWidth
      dir={language === "ar" ? "rtl" : "ltr"}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          background:
            "linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)"
        }
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          p: { xs: 3, sm: 4 }
        }}
      >
        <IconButton
          onClick={remindLater}
          aria-label={t.later}
          sx={{
            position: "absolute",
            right: language === "ar" ? "auto" : 12,
            left: language === "ar" ? 12 : "auto",
            top: 12
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            width: 58,
            height: 58,
            borderRadius: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(135deg, #e95a13, #e95a13)",
            mb: 2.5
          }}
        >
          <WorkspacePremiumRoundedIcon
            sx={{ color: "#fff", fontSize: 32 }}
          />
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            color: "#e95a13",
            fontSize: 14,
            mb: 0.7
          }}
        >
          {t.badge}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 26, sm: 30 },
            lineHeight: 1.15,
            mb: 1.5
          }}
        >
          {t.title}
        </Typography>

        <Typography
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {t.subtitle}
        </Typography>

        <Feature
          icon={<VisibilityOutlinedIcon />}
          text={t.visitors}
        />

        <Feature
          icon={<SearchRoundedIcon />}
          text={t.search}
        />

        <Feature
          icon={<HandshakeOutlinedIcon />}
          text={t.matching}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={discoverPremium}
          sx={{
            mt: 3,
            py: 1.4,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 16,
            background:
              "linear-gradient(135deg, #e95a13, #e95a13)"
          }}
        >
          {t.discover}
        </Button>

        <Button
          fullWidth
          onClick={remindLater}
          sx={{
            mt: 1,
            textTransform: "none",
            color: "text.secondary"
          }}
        >
          {t.later}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function Feature({ icon, text }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        my: 1.7
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "12px",
          backgroundColor: "rgba(99,91,255,.08)",
          color: "#e95a13",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {icon}
      </Box>

      <Typography sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
    </Box>
  );
}